import type { CarePlanDto } from "@repo/data-store";
import { completeCarePlan, holdCarePlan, revokeCarePlan } from "@repo/data-store";
import { DataList } from "@repo/ui/components/data-list";
import { EmptyState } from "@repo/ui/components/empty-state";
import { Button } from "@repo/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import { Link } from "@tanstack/react-router";
import { ClipboardList, MoreVerticalIcon } from "lucide-react";
import type { ReactNode } from "react";
import { CarePlanStatusBadge } from "@/features/cases/components/care-plan-status-badge";
import { formatDate } from "@/shared/lib/date-formats";

function Root({ children }: { children: ReactNode }) {
  return <div className="space-y-3">{children}</div>;
}

function Header({ title = "Care plans" }: { title?: string }) {
  return <h2 className="text-sm font-semibold">{title}</h2>;
}

function Body({ children }: { children: ReactNode }) {
  return <DataList>{children}</DataList>;
}

function Empty({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col">
      <EmptyState className="m-auto">
        <EmptyState.Icon>
          <ClipboardList className="size-4" />
        </EmptyState.Icon>
        <EmptyState.Content>
          <EmptyState.Title>No care plans yet</EmptyState.Title>
          <EmptyState.Description>
            Create a care plan when you’re ready to plan treatment for this patient.
          </EmptyState.Description>
        </EmptyState.Content>
        {children && <EmptyState.Actions>{children}</EmptyState.Actions>}
      </EmptyState>
    </div>
  );
}

interface ItemProps {
  carePlan: CarePlanDto;
  /** Patient id for building the detail link. */
  patientId: string;
  /** Display label when CarePlan.title is blank. */
  fallbackTitle?: string;
}

function Item({ carePlan, patientId, fallbackTitle = "Care plan" }: ItemProps) {
  const title = carePlan.title?.trim() || fallbackTitle;
  const isCasePlan = !carePlan.visitId;
  const status = carePlan.status;

  const parts = [
    isCasePlan ? "Case plan" : "Visit plan",
    `${carePlan.goals.length} goal${carePlan.goals.length === 1 ? "" : "s"}`,
    `${carePlan.activities.length} activit${carePlan.activities.length === 1 ? "y" : "ies"}`,
  ];
  if (carePlan.addresses?.length) {
    parts.push(`${carePlan.addresses.length} condition${carePlan.addresses.length === 1 ? "" : "s"}`);
  }

  const started = carePlan.period?.start ?? carePlan.created;

  return (
    <DataList.NavigatingRow
      label={title}
      renderLink={(props) => (
        <Link
          {...props}
          to="/patients/$patientId/care-plans/$carePlanId"
          params={{ patientId, carePlanId: carePlan.carePlanId }}
        />
      )}
    >
      <DataList.Primary>
        <DataList.Title>{title}</DataList.Title>
        <DataList.Subtitle>{parts.join(" · ")}</DataList.Subtitle>
      </DataList.Primary>
      <DataList.Status>
        <CarePlanStatusBadge status={status} />
      </DataList.Status>
      <DataList.Meta>{started ? `Started ${formatDate(started)}` : null}</DataList.Meta>
      <DataList.Actions>
        <RowMenu status={status} carePlanId={carePlan.carePlanId} />
      </DataList.Actions>
    </DataList.NavigatingRow>
  );
}

function RowMenu({ status, carePlanId }: { status: CarePlanDto["status"]; carePlanId: CarePlanDto["carePlanId"] }) {
  const canHold = status === "active";
  const canComplete = status === "active" || status === "on-hold";
  const canRevoke = status === "active" || status === "on-hold";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" size="icon-xs" aria-label="Care plan actions">
            <MoreVerticalIcon />
          </Button>
        }
      />
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled={!canHold} onClick={() => holdCarePlan({ carePlanId })}>
          Hold
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!canComplete} onClick={() => completeCarePlan({ carePlanId })}>
          Complete
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!canRevoke} onClick={() => revokeCarePlan({ carePlanId })}>
          Revoke
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const CarePlanList = Object.assign(Root, {
  Header,
  Body,
  Empty,
  Item,
});

/** Sort: case plans (no visitId) first; within group, draft → active → others, then by id desc. */
export function sortCarePlans(plans: CarePlanDto[]): CarePlanDto[] {
  const statusRank: Record<CarePlanDto["status"], number> = {
    draft: 0,
    active: 1,
    "on-hold": 2,
    completed: 3,
    revoked: 4,
    "entered-in-error": 5,
    unknown: 6,
  };
  return [...plans].sort((a, b) => {
    const aCase = !a.visitId ? 0 : 1;
    const bCase = !b.visitId ? 0 : 1;
    if (aCase !== bCase) return aCase - bCase;
    const sa = statusRank[a.status] ?? 99;
    const sb = statusRank[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return a.carePlanId < b.carePlanId ? 1 : -1;
  });
}
